import React, { createContext, useCallback, useContext, useMemo } from "react";
import { deleteVal, getVal, setVal } from "oolib";
import { useQueryClient } from "react-query";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useGetAllOptionsForDimension1 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension1";
import { useArithmeticExpression } from "../../utils/ArithmeticExpressionInput/useArithmeticExpression";
import { getWidgetContentTypes, pruneStaleWidgetFilters } from "../../utils/getWidgetContentTypes";
import { DATA_KEYS } from "./DATA_KEYS";
import { getVisibilityKeys } from "../../utils/visibilityState";

const PieChartConfigContext = createContext(null);

export const usePieChartConfig = () => {
  const ctx = useContext(PieChartConfigContext);
  if (!ctx) throw new Error("usePieChartConfig must be used within PieChartConfigProvider");
  return ctx;
};

export const PieChartConfigProvider = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  showNumberInputsInOptions = true,
  dimensionsInputPropsConfig = {},
  children,
}) => {
  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");
  const { SELF_MANAGED_BASE_CONFIGS, annotationTagsCount } = useGetQueryData("platformConfigs");
  // Clear this widget's cached data on a data-field change so combine-segments
  // (stepper max + default seeding) reads the NEW field's segment count, not a
  // stale slice from the previous field. Same pattern as WidgetBarChart.
  const { clearWidgetDataStore } = useReportLandingContext() || {};

  // --- updateWidget helper ---
  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const widgetType = widgetConfig?.comp || 'WidgetPieChart';
      const prevContentTypes = getWidgetContentTypes(widgetConfig, widgetType);
      widgetConfig = updaterFn(widgetConfig);
      // A write that changes which datasets the widget reads drops the widget
      // filters that belonged to a dataset it left (see pruneStaleWidgetFilters).
      widgetConfig = pruneStaleWidgetFilters(widgetConfig, prevContentTypes, widgetType);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  // --- Options ---
  const { options: optionsForDim1 } = useGetAllOptionsForDimension1({ showNumberInputsInOptions });

  // --- Pie Chart Type ---
  const pieChartType = useMemo(() => {
    const stored = getVal(thisWidgetConfig, DATA_KEYS.pieChartType);
    if (stored) return stored;
    // Auto-detect: if dimension2+ exists, treat as multiLayer
    if (getVal(thisWidgetConfig, DATA_KEYS.dimension2)) return 'multiLayer';
    return 'singleLayer';
  }, [thisWidgetConfig]);

  const clearDimensionConfig = useCallback((w, dimNum) => {
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}`]);
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}CustomRanges`]);
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}NumberFieldRepresentation`]);
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}HistogramBucketMode`]);
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}NumberOfBuckets`]);
    w = deleteVal(w, DATA_KEYS[`dimension${dimNum}BucketSize`]);
    w = deleteVal(w, DATA_KEYS[`hiddenDim${dimNum}Datapoints`]);
    w = deleteVal(w, DATA_KEYS[`dim${dimNum}VisibilityPreset`]);
    w = deleteVal(w, DATA_KEYS[`dim${dimNum}VisibilityN`]);
    return w;
  }, []);

  const handlePieChartTypeChange = useCallback((newType) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.pieChartType, newType);
      // Clear all dimensions + related config when switching types
      for (let i = 1; i <= 4; i++) {
        w = clearDimensionConfig(w, i);
      }
      // Combine segments is single-layer only, so drop it when the type changes.
      w = deleteVal(w, DATA_KEYS.combineSegments);
      w = deleteVal(w, DATA_KEYS.numberOfSegments);
      // Clear auto-title
      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        w = deleteVal(w, DATA_KEYS.field1);
      }
      return w;
    });
  }, [updateWidget]);

  // --- Derived ---
  const dim1IsGroupQuestion = useMemo(() => {
    const dim1Value = getVal(thisWidgetConfig, DATA_KEYS.dimension1);
    return dim1Value?.comp === 'GroupQuestionsInputSingle';
  }, [thisWidgetConfig]);

  const widgetType = thisWidgetConfig?.comp || 'WidgetPieChart';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  // --- Handlers ---
  const handleDimension1Change = useCallback((v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.dimension1, v);

      // Clear all dependent dimensions + related config
      for (let i = 2; i <= 4; i++) {
        w = clearDimensionConfig(w, i);
      }
      // Clear dim1's own bucket/visibility config
      w = deleteVal(w, DATA_KEYS.dimension1CustomRanges);
      w = deleteVal(w, DATA_KEYS.dimension1NumberFieldRepresentation);
      w = deleteVal(w, DATA_KEYS.dimension1HistogramBucketMode);
      w = deleteVal(w, DATA_KEYS.dimension1NumberOfBuckets);
      w = deleteVal(w, DATA_KEYS.dimension1BucketSize);
      w = deleteVal(w, DATA_KEYS.hiddenDim1Datapoints);
      // Mode + dynamic settings reset with the dimension too. Keys come from
      // getVisibilityKeys so they can't drift from what the writers use.
      {
        const visKeys = getVisibilityKeys({ dataKeys: DATA_KEYS, dimension: 1 });
        w = deleteVal(w, visKeys.modeKey);
        w = deleteVal(w, visKeys.dynamicPresetKey);
      }
      w = deleteVal(w, DATA_KEYS.dim1VisibilityPreset);
      w = deleteVal(w, DATA_KEYS.dim1VisibilityN);
      // Reset combine-segments count so max/default recompute for the new field's segments.
      w = deleteVal(w, DATA_KEYS.numberOfSegments);

      // Auto-title
      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        w = setVal(w, DATA_KEYS.field1, v?.display || v?.valuePath || '');
      }
      return w;
    });
    // New field → drop cached data so the combine-segments default/max recompute
    // from the new field's result instead of the previous one's.
    clearWidgetDataStore?.(thisWidgetConfig?.id);
  }, [updateWidget, clearWidgetDataStore, thisWidgetConfig?.id]);

  // Generic handler for dimensions 2-4
  const handleDimensionChange = useCallback((dimNum, v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS[`dimension${dimNum}`], v);

      // Clear downstream dimensions + their related config
      for (let i = dimNum + 1; i <= 4; i++) {
        w = clearDimensionConfig(w, i);
      }

      // Clear own bucket/visibility config
      w = deleteVal(w, DATA_KEYS[`dimension${dimNum}CustomRanges`]);
      w = deleteVal(w, DATA_KEYS[`dimension${dimNum}NumberFieldRepresentation`]);
      w = deleteVal(w, DATA_KEYS[`dimension${dimNum}HistogramBucketMode`]);
      w = deleteVal(w, DATA_KEYS[`dimension${dimNum}NumberOfBuckets`]);
      w = deleteVal(w, DATA_KEYS[`dimension${dimNum}BucketSize`]);
      w = deleteVal(w, DATA_KEYS[`hiddenDim${dimNum}Datapoints`]);
      w = deleteVal(w, DATA_KEYS[`dim${dimNum}VisibilityPreset`]);
      w = deleteVal(w, DATA_KEYS[`dim${dimNum}VisibilityN`]);

      return w;
    });
  }, [updateWidget, clearDimensionConfig]);

  // Clear a dimension and all downstream dimensions + related config
  const handleDimensionClear = useCallback((dimNum) => {
    updateWidget((w) => {
      for (let i = dimNum; i <= 4; i++) {
        w = clearDimensionConfig(w, i);
      }
      return w;
    });
  }, [updateWidget, clearDimensionConfig]);

  // --- Arithmetic expression state ---
  const dim1Value = getVal(thisWidgetConfig, DATA_KEYS.dimension1);

  const arithmeticState = useArithmeticExpression({
    allOptions: optionsForDim1,
    value: dim1Value,
    onChange: handleDimension1Change,
    disabled: false,
  });

  // --- Multi-layer derived state ---
  const multiLayerState = useMemo(() => {
    const layers = [];
    for (let i = 1; i <= 4; i++) {
      const dim = getVal(thisWidgetConfig, DATA_KEYS[`dimension${i}`]);
      if (dim) layers.push({ dimNum: i, value: dim });
    }
    // Count of active layers (at least 1 slot always visible)
    const layerCount = Math.max(layers.length, 1);
    const canAddLayer = layerCount < 4;
    // Find next available slot
    const nextSlot = layerCount + 1;
    return { layers, layerCount, canAddLayer, nextSlot };
  }, [thisWidgetConfig]);

  // --- Context value ---
  const value = useMemo(() => ({
    widgetConfig: thisWidgetConfig,
    updateWidget,
    dataKeys: DATA_KEYS,
    setConfig,
    underEditWidgetIdx,
    // Options
    optionsForDim1,
    allTpls,
    SELF_MANAGED_BASE_CONFIGS,
    annotationTagsCount,
    contentTypesForFilters,
    // Config flags
    dimensionsInputPropsConfig,
    // Derived
    dim1IsGroupQuestion,
    // Pie chart type
    pieChartType,
    handlePieChartTypeChange,
    // Arithmetic (single layer)
    arithmeticState,
    // Multi-layer
    multiLayerState,
    // Handlers
    handleDimension1Change,
    handleDimensionChange,
    handleDimensionClear,
  }), [
    thisWidgetConfig, updateWidget, setConfig, underEditWidgetIdx,
    optionsForDim1, allTpls, SELF_MANAGED_BASE_CONFIGS, annotationTagsCount,
    contentTypesForFilters, dimensionsInputPropsConfig, dim1IsGroupQuestion,
    pieChartType, handlePieChartTypeChange,
    arithmeticState, multiLayerState,
    handleDimension1Change, handleDimensionChange, handleDimensionClear,
  ]);

  return (
    <PieChartConfigContext.Provider value={value}>
      {children}
    </PieChartConfigContext.Provider>
  );
};
